// networkRetryInterceptor.ts
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

type RetryConfig = InternalAxiosRequestConfig & {
  __networkRetryCount?: number
}

type NetworkRetryOptions = {
  maxRetries?: number
  retryStatusCodes?: number[]
  includeTimeouts?: boolean
}

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export const createNetworkRetryInterceptor =
  (client: AxiosInstance, options: NetworkRetryOptions = {}) =>
    async (error: AxiosError) => {
      const config = error.config as RetryConfig | undefined

      if (!config) return Promise.reject(error)

      const maxRetries = options.maxRetries ?? 1
      const retryStatusCodes = options.retryStatusCodes ?? []

      const isNoResponseNetworkError =
        !error.response &&
        (
          error.code === 'ERR_NETWORK' ||
          error.message === 'Network Error' ||
          error.request?.status === 0
        )

      const isTimeout =
        options.includeTimeouts === true &&
        (
          error.code === 'ECONNABORTED' ||
          error.code === 'ETIMEDOUT'
        )

      const isRetryableStatus =
        error.response?.status !== undefined &&
        retryStatusCodes.includes(error.response.status)

      const isCancelled =
        error.code === 'ERR_CANCELED' ||
        axios.isCancel(error)

      if ((!isNoResponseNetworkError && !isTimeout && !isRetryableStatus) || isCancelled) {
        return Promise.reject(error)
      }

      config.__networkRetryCount = config.__networkRetryCount ?? 0

      if (config.__networkRetryCount >= maxRetries) {
        return Promise.reject(error)
      }

      config.__networkRetryCount += 1

      // Jitter retry to prevent multiple requests from retrying in the same order which could cause them to fail again due to the same network issue.
      await wait(300 + Math.floor(Math.random() * 700))

      return client(config)
    }
